// Chart color utilities using design tokens
// Provides consistent colors for charts and data visualization

export const chartColors = {
  // Primary brand colors
  primary: 'var(--brand-primary)',
  secondary: 'var(--brand-secondary)',
  success: 'var(--brand-success)',
  info: 'var(--brand-info)',
  warning: 'var(--brand-warning)',
  error: 'var(--brand-error)',
  
  // Extended palette for multiple data series
  blue: 'var(--brand-primary)',
  green: 'var(--brand-success)',
  orange: 'var(--brand-warning)',
  red: 'var(--brand-error)',
  purple: 'var(--brand-80)',
  teal: 'var(--brand-50)',
  pink: 'var(--brand-accent-50)',
  indigo: 'var(--brand-90)',
  
  // Neutral colors for backgrounds and borders
  background: 'var(--bg-primary)',
  backgroundSecondary: 'var(--bg-secondary)',
  border: 'var(--border-primary)',
  text: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textTertiary: 'var(--text-tertiary)',
} as const;

// Color palette for multiple data series
export const chartColorPalette = [
  chartColors.blue,
  chartColors.green,
  chartColors.orange,
  chartColors.red,
  chartColors.purple,
  chartColors.teal,
  chartColors.pink,
  chartColors.indigo,
];

// Semantic color mapping for different data types
export const semanticColors = {
  positive: chartColors.success,
  negative: chartColors.error,
  neutral: chartColors.textSecondary,
  highlight: chartColors.primary,
  muted: chartColors.textTertiary,
} as const;

// Chart-specific color configurations
export const chartConfig = {
  // Area chart colors
  area: {
    primary: chartColors.primary,
    secondary: chartColors.secondary,
    fill: 'rgba(0, 133, 202, 0.1)', // Semi-transparent brand color
  },
  
  // Line chart colors
  line: {
    primary: chartColors.primary,
    secondary: chartColors.success,
    tertiary: chartColors.warning,
  },
  
  // Bar chart colors
  bar: {
    primary: chartColors.primary,
    secondary: chartColors.success,
    tertiary: chartColors.warning,
    quaternary: chartColors.error,
  },
  
  // Pie chart colors
  pie: chartColorPalette,
  
  // Scatter plot colors
  scatter: {
    primary: chartColors.primary,
    secondary: chartColors.success,
  },
} as const;

// Helper function to get color by index
export const getChartColor = (index: number): string => {
  return chartColorPalette[index % chartColorPalette.length];
};

// Helper function to get semantic color
export const getSemanticColor = (type: keyof typeof semanticColors): string => {
  return semanticColors[type];
};

// Helper function to get chart config color
export const getChartConfigColor = (chartType: keyof typeof chartConfig, colorKey: string): string => {
  const config = chartConfig[chartType];
  if (typeof config === 'object' && colorKey in config) {
    return (config as any)[colorKey];
  }
  return chartColors.primary; // fallback
};
